import { GridApi, RowNode } from '@ag-grid-enterprise/all-modules';
import { Box, Button, ButtonGroup, IconButton, InputBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Clear, KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';

import { useTableGridApiState } from '../../../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { CONSTANTS } from '../../../../../../../core/config/constants';
import {
  BorderWidthWithColor,
  FontSizes,
  ResolutionColors,
} from '../../../../../../../core/config/mainTheme';
import { useBenchmarkDispatch, useBenchmarkState } from '../../../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../../Context/BenchmarkContext.types';

interface NavigationHeaderProps {
  gridApi?: GridApi;
}

const MiniProgressBar: React.FC<{
  accepted: number;
  pending: number;
  rejected: number;
  total: number;
  acceptColor: string;
  reviewColor: string;
  rejectColor: string;
}> = ({ accepted, pending, rejected, total, acceptColor, reviewColor, rejectColor }) => {
  if (total === 0) return null;
  const acceptedPercent = (accepted / total) * 100;
  const pendingPercent = (pending / total) * 100;
  const rejectedPercent = (rejected / total) * 100;
  // Match Material-UI button height (default 40px)
  const progressBarHeight = 36;
  return (
    <div
      style={{
        width: 120,
        height: progressBarHeight,
        borderRadius: 4,
        overflow: 'hidden',
        background: '#eee',
        marginLeft: 8,
        marginRight: 8,
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      <div
        style={{
          width: `${acceptedPercent}%`,
          height: '100%',
          background: acceptColor,
          display: 'inline-block',
        }}
      />
      <div
        style={{
          width: `${pendingPercent}%`,
          height: '100%',
          background: reviewColor,
          display: 'inline-block',
        }}
      />
      <div
        style={{
          width: `${rejectedPercent}%`,
          height: '100%',
          background: rejectColor,
          display: 'inline-block',
        }}
      />
    </div>
  );
};

const NavigationHeader: React.FC<NavigationHeaderProps> = () => {
  const classes = useStyles();
  const { companies, pagination, isLoading, benchwiseState, companyFilter } = useBenchmarkState();
  const dispatch = useBenchmarkDispatch();
  const { activeCompanyId } = useBenchmarkState();
  const { tableGridApi } = useTableGridApiState();

  const [localFilter, setLocalFilter] = useState(companyFilter);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state when context resets (e.g. navigating to a new benchmark)
  useEffect(() => {
    setLocalFilter(companyFilter);
  }, [companyFilter]);

  const handleFilterChange = (value: string) => {
    setLocalFilter(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch({ type: BenchmarkContextActionTypes.SetCompanyFilter, payload: value });
    }, 300);
  };
  const [visibleRows, setVisibleRows] = useState<number>(0);
  const [prevDisable, setPrevDisable] = useState<boolean>(true);
  const [nextDisable, setNexDisable] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<any>(null);

  const getFirstIndex = () => {
    return (pagination.page > 0 ? pagination.page - 1 : 0) * pagination.size;
  };

  const getLastIndex = () => {
    return getFirstIndex() + visibleRows;
  };

  useEffect(() => {
    if (companies) {
      setVisibleRows(companies.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, pagination.page]);

  useEffect(() => {
    setPrevDisable(
      selectedIndex === getFirstIndex() ||
        selectedIndex < getFirstIndex() ||
        selectedIndex > getLastIndex() ||
        selectedIndex === null,
    );
    setNexDisable(selectedIndex === visibleRows - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, visibleRows, pagination.page]);

  useEffect(() => {
    if (tableGridApi) {
      tableGridApi.forEachNode((rowNode: RowNode) => {
        if (activeCompanyId === rowNode?.data?.companyInfo?.id) {
          setSelectedIndex(rowNode.rowIndex);
        }
      });
    }
  }, [tableGridApi, activeCompanyId, companies]);

  useEffect(() => {
    if (tableGridApi && selectedIndex !== null) {
      tableGridApi.forEachNode((rowNode: RowNode) => {
        if (selectedIndex === rowNode.rowIndex) {
          rowNode.setSelected(true);
        }
      });
    }
  }, [tableGridApi, selectedIndex, isLoading]);

  const handleClick = (direction: string) => {
    if (tableGridApi) {
      let rowNode = tableGridApi.getSelectedNodes()[0]
        ? tableGridApi.getSelectedNodes()[0]
        : tableGridApi.getRowNode(getFirstIndex().toString());

      if (!rowNode) return; // Prevent error if rowNode is undefined

      let rowIndex = rowNode.rowIndex;

      if (rowIndex < getFirstIndex() || rowIndex > getLastIndex() || selectedIndex === null) {
        rowIndex = getFirstIndex() - 1;
      }

      switch (direction) {
        case CONSTANTS.NAVIGATE_NEXT:
          rowNode = tableGridApi.getRowNode(`${rowIndex + 1}`);
          break;
        case CONSTANTS.NAVIGATE_PREV:
          rowNode = tableGridApi.getRowNode(`${rowIndex - 1}`);
          break;
      }

      if (!rowNode) return; // Prevent error if rowNode is undefined after navigation

      setSelectedIndex(rowNode.rowIndex);
      dispatch({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: rowNode.data.companyInfo.id,
      });
    }
  };

  // Mini progress bar component

  // Helper for benchwiseValidate state
  const isBenchwiseValidateDisabled =
    benchwiseState?.benchwiseStage === 'FAST_REVIEW' ||
    benchwiseState?.benchwiseStage === 'SLOW_REVIEW';
  const showMiniProgressBar = isBenchwiseValidateDisabled && (benchwiseState?.totalCount ?? 0) > 0;

  return (
    <Box className={classes.root}>
      {showMiniProgressBar && (
        <MiniProgressBar
          accepted={benchwiseState?.acceptedCount ?? 0}
          pending={benchwiseState?.pendingCount ?? 0}
          rejected={benchwiseState?.rejectedCount ?? 0}
          total={benchwiseState?.totalCount ?? 0}
          acceptColor={ResolutionColors.Accepted}
          reviewColor={ResolutionColors.Pending}
          rejectColor={ResolutionColors.Rejected}
        />
      )}
      <InputBase
        value={localFilter}
        onChange={(e) => handleFilterChange(e.target.value)}
        placeholder="Search company..."
        className={classes.filterInput}
        endAdornment={
          localFilter ? (
            <IconButton size="small" onClick={() => handleFilterChange('')}>
              <Clear fontSize="small" />
            </IconButton>
          ) : null
        }
      />
      <ButtonGroup size="small" className={classes.buttonGroup}>
        <Button
          onClick={() => handleClick(CONSTANTS.NAVIGATE_PREV)}
          className={prevDisable ? 'disabled' : ''}
        >
          <KeyboardArrowLeft />
        </Button>
        <Button
          onClick={() => handleClick(CONSTANTS.NAVIGATE_NEXT)}
          className={nextDisable ? 'disabled' : ''}
        >
          <KeyboardArrowRight />
        </Button>
      </ButtonGroup>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    backgroundColor: theme.palette.secondary.main,
    height: theme.spacing(11),
    border: BorderWidthWithColor(1, theme.palette.divider),
  },
  filterInput: {
    flex: 1,
    padding: theme.spacing(0, 1),
    fontSize: FontSizes.size.small,
  },
  buttonGroup: {
    borderRadius: 0,
    marginLeft: 'auto',
    '& .MuiButton-root': {
      borderRadius: 0,
      borderTop: 0,
      borderBottom: 0,
      color: theme.palette.secondary.dark,
      borderRight: 'none',
      borderLeft: BorderWidthWithColor(1, theme.palette.divider),
      '&.disabled': {
        pointerEvents: 'none',
      },
    },
  },
  refinementBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  refinementButton: {
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontSize: FontSizes.size.smallest,
  },
}));
export default NavigationHeader;
